/**
 * Format price in pence to pounds and pence display with three decimal places
 */
export function formatPrice(pence?: number): string {
  if (pence === undefined || pence === null) return 'N/A';
  return `£${(pence / 100).toFixed(3)}`;
}

/**
 * Format distance in miles
 */
export function formatDistance(miles: number): string {
  if (miles < 0.1) return `${Math.round(miles * 5280)} ft`;
  return `${miles.toFixed(1)} mi`;
}

/**
 * Format time duration in minutes
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format travel time duration in minutes to human-readable string
 * @param minutes - Duration in minutes
 * @returns Formatted string like "2 min", "15 min", "1h 5min"
 */
export function formatTravelTime(minutes: number): string {
  if (minutes < 1) {
    return "< 1 min";
  }
  
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}min`;
}

/**
 * Format distance and travel time together for display
 * @param distance - Distance in miles
 * @param travelTimeMinutes - Travel time in minutes (optional)
 * @returns Formatted string like "0.4 mi • 2 min" or "0.4 mi"
 */
export function formatDistanceAndTime(distance: number, travelTimeMinutes?: number): string {
  const distanceStr = formatDistance(distance);
  
  if (travelTimeMinutes !== undefined && travelTimeMinutes > 0) {
    const timeStr = formatTravelTime(travelTimeMinutes);
    return `${distanceStr} • ${timeStr}`;
  }
  
  return distanceStr;
}

/**
 * Format fuel efficiency (MPG)
 */
export function formatMPG(mpg: number): string {
  return `${mpg.toFixed(1)} MPG`;
}

/**
 * Format cost per mile
 */
export function formatCostPerMile(pencePerMile: number): string {
  return `${pencePerMile.toFixed(1)}p/mi`;
}

/**
 * Format address for display
 */
export function formatAddress(address: string, maxLength = 50): string {
  if (address.length <= maxLength) return address;
  return `${address.substring(0, maxLength - 3)}...`;
}

/**
 * Format postcode
 */
export function formatPostcode(postcode: string): string {
  // UK postcode formatting (e.g., "SW1A1AA" -> "SW1A 1AA", "M11AA" -> "M1 1AA")
  const cleaned = postcode.replace(/\s+/g, '').toUpperCase();
  if (cleaned.length >= 5 && cleaned.length <= 7) {
    // Split between outward code and inward code
    // Inward code is always last 3 characters, outward code is the rest
    const inward = cleaned.slice(-3);
    const outward = cleaned.slice(0, -3);
    return `${outward} ${inward}`;
  }
  return postcode.toUpperCase();
}

/**
 * Format date/time
 */
export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

/**
 * Format relative time (e.g., "5 minutes ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return formatDateTime(dateString);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format fuel type display name
 */
export function formatFuelType(fuelCode: string): string {
  const fuelNames: Record<string, string> = {
    E10: 'Unleaded E10',
    E5: 'Unleaded E5',
    B7: 'Diesel',
    SDV: 'Premium Diesel',
  };
  
  return fuelNames[fuelCode] || fuelCode;
}

/**
 * Format brand name for consistent display
 */
export function formatBrandName(brand: string): string {
  // Handle common brand name variations
  const brandMappings: Record<string, string> = {
    'BP': 'BP',
    'SHELL': 'Shell',
    'ESSO': 'Esso',
    'TESCO': 'Tesco',
    'ASDA': 'Asda',
    'SAINSBURYS': "Sainsbury's",
    'MORRISONS': 'Morrisons',
  };
  
  const upperBrand = brand.toUpperCase();
  return brandMappings[upperBrand] || brand;
}

/**
 * Format vehicle type display name
 */
export function formatVehicleType(type: string): string {
  const typeNames: Record<string, string> = {
    petrol: 'Petrol',
    diesel: 'Diesel',
    hybrid: 'Hybrid',
    electric: 'Electric',
  };
  
  return typeNames[type] || type;
}

/**
 * Format number with thousands separator
 */
export function formatNumber(num: number, decimals = 0): string {
  return num.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Calculate distance between two geographic points using Haversine formula
 * Returns distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}